import { api } from './client'

// ---------- tipuri — oglindesc DTO-urile din Cipral.Application/Comercial/ComenziModels.cs ----------

export type OrderStatus =
  | 'Draft' | 'Confirmed' | 'InProduction' | 'PartiallyDelivered' | 'FullyDelivered'
  | 'Invoiced' | 'Closed' | 'Cancelled' | 'Suspended'

export interface OrderLine {
  id: string
  lineNumber: number
  articleId: string
  articleCode: string
  articleDescription: string
  quantityThousands: number
  pricePer1000: number
  discountPercent: number
  vatRateId: string | null
  vatRatePercent: number | null
  lineDeliveryTerm: string | null
  lineValue: number
  deliveredQuantity: number
  remainingQuantity: number
  requireCompleteSetDelivery: boolean
}

export type SaveOrderLinePayload = Pick<
  OrderLine, 'articleId' | 'quantityThousands' | 'pricePer1000' | 'discountPercent' | 'vatRateId' | 'lineDeliveryTerm'
>

export interface OrderListItem {
  id: string
  orderNumber: string
  orderDate: string
  clientId: string
  clientName: string
  agentUserName: string
  status: OrderStatus
  currency: string
  total: number
}

export interface OrderDetail {
  id: string
  orderNumber: string
  orderDate: string
  clientId: string
  clientName: string
  deliveryAddress: string
  billingAddress: string
  clientPoReference: string | null
  agentUserId: string
  agentUserName: string
  paymentTerms: string | null
  currency: string
  requestedDeliveryDate: string
  notes: string | null
  status: OrderStatus
  confirmedAt: string | null
  creditLimitOverridden: boolean
  creditLimitOverrideReason: string | null
  belowCostOverridden: boolean
  belowCostOverrideReason: string | null
  bomFeasibilityOverridden: boolean
  bomFeasibilityOverrideReason: string | null
  sourceOfferId: string | null
  fulfillmentWarehouseId: string | null
  fulfillmentWarehouseName: string | null
  workPointId: string
  workPointName: string
  lines: OrderLine[]
  total: number
}

export interface SaveOrderPayload {
  orderDate: string
  clientId: string
  deliveryAddressId: string
  billingAddressId: string
  clientPoReference: string | null
  agentUserId: string
  paymentTerms: string | null
  currency: string
  requestedDeliveryDate: string
  notes: string | null
  workPointId: string
  fulfillmentWarehouseId: string | null
  lines: SaveOrderLinePayload[]
  belowCostOverrideReason: string | null
}

export interface OrderWorkPointRef { id: string; code: string; name: string }
export const listOrderWorkPoints = () => api.get<OrderWorkPointRef[]>('/comercial/comenzi/puncte-lucru').then((r) => r.data)

export interface CreditCheckFailed { message: string; outstanding: number; creditLimit: number; orderTotal: number }

// ---------- FR-MDP-008 — blocare vânzare sub cost ----------

export interface BelowCostViolation { articleCode: string; pricePer1000: number; standardCost: number }
export interface BelowCostViolationsResponse { message: string; violations: BelowCostViolation[] }

// ---------- FR-CMD-005 — necesar de producție ----------

export interface ProductionNeed {
  articleId: string; articleCode: string; articleDescription: string
  orderedQuantity: number; availableQuantity: number; shortfallQuantity: number
}
export interface ConfirmOrderResult { pendingApproval: boolean; productionNeeds: ProductionNeed[] }

// ---------- FR-CMD-011…013/016/017 — fezabilitate multi-nivel pe BOM ----------

export interface BomFeasibilityLine {
  articleId: string; articleCode: string; articleDescription: string; requiredQuantity: number
  availableQuantity: number; shortfallQuantity: number; suggestedAction: string; estimatedAvailabilityDate: string | null
  unitOfMeasureId: string
}
export interface BomFeasibilityReport {
  isFeasible: boolean; lines: BomFeasibilityLine[]
  criticalComponentArticleId: string | null; criticalComponentCode: string | null; criticalComponentEstimatedDate: string | null
}
export interface BomFeasibilityCheckFailed { message: string; report: BomFeasibilityReport }

export interface OrderDelivery { id: string; deliveredDate: string; quantityThousands: number; notes: string | null; recordedByUserName: string }
export type RecordDeliveryPayload = Pick<OrderDelivery, 'deliveredDate' | 'quantityThousands' | 'notes'>

export const listOrders = () => api.get<OrderListItem[]>('/comercial/comenzi').then((r) => r.data)
export const getOrder = (id: string) => api.get<OrderDetail>(`/comercial/comenzi/${id}`).then((r) => r.data)
export const createOrder = (payload: SaveOrderPayload) =>
  api.post<{ id: string }>('/comercial/comenzi', payload).then((r) => r.data)
export const updateOrder = (id: string, payload: SaveOrderPayload) => api.put(`/comercial/comenzi/${id}`, payload)
export const confirmOrder = (id: string, creditOverrideReason?: string, bomFeasibilityOverrideReason?: string) =>
  api.post<ConfirmOrderResult>(`/comercial/comenzi/${id}/confirma`, {
    creditOverrideReason: creditOverrideReason ?? null, bomFeasibilityOverrideReason: bomFeasibilityOverrideReason ?? null,
  }).then((r) => r.data)
export const getProductionNeeds = (id: string) =>
  api.get<ProductionNeed[]>(`/comercial/comenzi/${id}/necesar-productie`).then((r) => r.data)
export const getBomFeasibility = (id: string) =>
  api.get<BomFeasibilityReport>(`/comercial/comenzi/${id}/fezabilitate-bom`).then((r) => r.data)
export const cancelOrder = (id: string) => api.post(`/comercial/comenzi/${id}/anuleaza`)
export const suspendOrder = (id: string) => api.post(`/comercial/comenzi/${id}/suspenda`)
export const reactivateOrder = (id: string) => api.post(`/comercial/comenzi/${id}/reactiveaza`)
export const setOrderStatus = (id: string, status: OrderStatus) => api.post(`/comercial/comenzi/${id}/status`, { status })

export const listDeliveries = (orderId: string, lineId: string) =>
  api.get<OrderDelivery[]>(`/comercial/comenzi/${orderId}/linii/${lineId}/livrari`).then((r) => r.data)
export const recordDelivery = (orderId: string, lineId: string, payload: RecordDeliveryPayload) =>
  api.post(`/comercial/comenzi/${orderId}/linii/${lineId}/livrari`, payload)

// ---------- Catalog de rapoarte, „Comenzi client în curs" (§12.2 #5) ----------

export interface OrderInProgress {
  id: string; orderNumber: string; orderDate: string; clientId: string; clientName: string
  status: OrderStatus; deliveryTerm: string; isLate: boolean; total: number; currency: string
}

export const getOrdersInProgress = () => api.get<OrderInProgress[]>('/comercial/comenzi/in-curs').then((r) => r.data)
export const getOrdersInProgressExportUrl = () =>
  api.get<Blob>('/comercial/comenzi/in-curs/export', { responseType: 'blob' }).then((r) => r.data)
